import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { useFormats } from "@ryde/shared";

import {
  INVESTIGATION_HISTORY_ICON,
  RIDE_INVESTIGATION_SEVERITY_BG,
  RIDE_INVESTIGATION_SEVERITY_COLOR,
} from "../investigation.constants";
import { HistoryRowStyled } from "../investigation.history.styled";
import type {
  IInvestigationAuditChanges,
  IInvestigationHistoryRow,
} from "../investigation.models";
import { InvestigationHistoryEntryDetails } from "./investigation.history-entry-details";
import { InvestigationHistoryFieldDetails } from "./investigation.history-field-details";
import { InvestigationHistoryPayload } from "./investigation.history-payload";
import { InvestigationHistoryRowHead } from "./investigation.history-row-head";

interface IInvestigationHistoryRowProps {
  row: IInvestigationHistoryRow;
  isActive: boolean;
  /** Moves the replay to the row's moment and rings its stops on the map. */
  onSelect: (row: IInvestigationHistoryRow) => void;
  /** What the row's audited change did, when it has one. */
  auditChanges?: IInvestigationAuditChanges;
}

/** One History row: icon, label and time, then the source-specific details. */
export const InvestigationHistoryRow: FC<IInvestigationHistoryRowProps> = ({
  row,
  isActive,
  onSelect,
  auditChanges,
}) => {
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const Icon = INVESTIGATION_HISTORY_ICON[row.iconKey];

  return (
    <HistoryRowStyled
      $color={RIDE_INVESTIGATION_SEVERITY_COLOR[row.severity]}
      $bg={RIDE_INVESTIGATION_SEVERITY_BG[row.severity]}
      $active={isActive}
      aria-current={isActive ? "true" : undefined}
    >
      <span className="history-icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="history-body">
        <InvestigationHistoryRowHead
          label={t(row.labelKey, row.labelParams)}
          time={formatDateTimeWithSecondsNormalized(
            new Date(row.tMs).toISOString() as TDateISO,
          )}
          onSeek={row.isSeekable ? () => onSelect(row) : undefined}
        />
        {row.meta && <div className="history-line">{row.meta}</div>}
        {row.entry && (
          <InvestigationHistoryEntryDetails
            entry={row.entry}
            auditChanges={auditChanges}
          />
        )}
        {row.entry?.supplier && (
          <InvestigationHistoryPayload supplier={row.entry.supplier} />
        )}
        {auditChanges && (
          <InvestigationHistoryFieldDetails details={auditChanges.details} />
        )}
      </div>
    </HistoryRowStyled>
  );
};
